import React from 'react';
import { Alert, Result, Spin, Statistic, Table, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useAuthStore } from '@/store/authStore';
import {
  filtersQueryKey,
  getSalesAnalyticsCollections,
  type SalesAnalyticsFilterParams,
} from '@/services/salesAnalytics.service';
import { SalesAnalyticsToolbar } from './SalesAnalyticsToolbar';

const { Title } = Typography;

function hasFinanceAccess(user: ReturnType<typeof useAuthStore.getState>['user']): boolean {
  const perms = user?.permissions ?? [];
  return Array.isArray(perms) && perms.some((p) => String(p).startsWith('finance.'));
}

export const SalesAnalyticsCollectionsTab: React.FC<{
  filters: SalesAnalyticsFilterParams;
  filterSummary: string;
}> = ({ filters, filterSummary }) => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const allowed = hasFinanceAccess(user);

  const query = useQuery({
    queryKey: ['sales-analytics', 'collections', filtersQueryKey(filters)],
    queryFn: () => getSalesAnalyticsCollections(filters),
    enabled: allowed,
  });

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Finance access required"
        subTitle="Collections analytics includes receipt amounts. Request finance.view or finance.reports.view."
      />
    );
  }

  if (query.isLoading) return <Spin />;
  if (query.isError) return <Alert type="error" message={(query.error as Error).message} />;
  const d = query.data!;

  const printRows = d.receipts.map((r) => ({
    voucher_number: r.voucher_number,
    receipt_date: r.receipt_date,
    customer: r.customer_name ?? '',
    amount: formatCurrency(r.amount),
    unallocated: formatCurrency(r.unallocated_amount),
  }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          Collections
        </Title>
        <SalesAnalyticsToolbar
          tab="collections"
          tabTitle="Collections"
          filters={filters}
          filterSummary={filterSummary}
          printColumns={[
            { key: 'voucher_number', label: 'Receipt' },
            { key: 'receipt_date', label: 'Date' },
            { key: 'customer', label: 'Customer' },
            { key: 'amount', label: 'Amount', align: 'right' },
            { key: 'unallocated', label: 'Unallocated', align: 'right' },
          ]}
          printRows={printRows}
          printKpiLines={[
            { label: 'Collections', value: formatCurrency(d.collections_total) },
            { label: 'Invoiced', value: formatCurrency(d.invoiced_total) },
            { label: 'Open AR', value: formatCurrency(d.open_ar_remaining) },
          ]}
        />
      </div>

      <Statistic title="Posted receipts (period)" value={formatCurrency(d.collections_total)} />
      <Statistic title="Invoiced (period)" value={formatCurrency(d.invoiced_total)} style={{ marginTop: 8 }} />
      <Statistic
        title="Collection rate"
        value={d.collection_rate_pct ?? '—'}
        suffix={d.collection_rate_pct != null ? '%' : undefined}
        style={{ marginTop: 8 }}
      />
      <Statistic title="Open AR remaining" value={formatCurrency(d.open_ar_remaining)} style={{ marginTop: 8 }} />

      <Title level={5} style={{ marginTop: 24 }}>
        Receipt vouchers
      </Title>
      <Table
        size="small"
        rowKey="receipt_voucher_id"
        dataSource={d.receipts}
        pagination={{ pageSize: 10 }}
        columns={[
          { title: 'Voucher', dataIndex: 'voucher_number' },
          { title: 'Date', dataIndex: 'receipt_date', width: 110 },
          { title: 'Customer', dataIndex: 'customer_name' },
          { title: 'Amount', dataIndex: 'amount', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: 'Allocated', dataIndex: 'allocated_amount', align: 'right', render: (v: number) => formatCurrency(v) },
          { title: 'Unallocated', dataIndex: 'unallocated_amount', align: 'right', render: (v: number) => formatCurrency(v) },
          {
            title: '',
            key: 'link',
            width: 80,
            render: (_: unknown, r) => <a onClick={() => navigate(r.link_path)}>View</a>,
          },
        ]}
      />
    </div>
  );
};
